import { matchPath } from "react-router-dom";

export interface Crumb {
  label: string;
  to?: string;
}

const SALES = { label: "Sales" };
const CUSTOMERS = { label: "Customers" };
const SETTINGS = { label: "Settings" };

/** First match wins, so specific paths come before their `:id` forms */
const ROUTES: { path: string; crumbs: Crumb[] }[] = [
  { path: "/dashboard", crumbs: [{ label: "Home" }, { label: "Overview" }] },
  { path: "/dashboard/my-day", crumbs: [{ label: "Home", to: "/dashboard" }, { label: "My Day" }] },
  { path: "/ask", crumbs: [{ label: "Home", to: "/dashboard" }, { label: "Ask AI" }] },
  { path: "/tasks", crumbs: [{ label: "Home" }, { label: "Tasks" }] },
  { path: "/calendar", crumbs: [{ label: "Home" }, { label: "Calendar" }] },

  { path: "/leads/import", crumbs: [SALES, { label: "Leads", to: "/leads" }, { label: "Import" }] },
  { path: "/leads/new", crumbs: [SALES, { label: "Leads", to: "/leads" }, { label: "New lead" }] },
  { path: "/leads/:id/edit", crumbs: [SALES, { label: "Leads", to: "/leads" }, { label: "Edit lead" }] },
  { path: "/leads/:id", crumbs: [SALES, { label: "Leads", to: "/leads" }, { label: "Lead" }] },
  { path: "/leads", crumbs: [SALES, { label: "Leads" }] },
  { path: "/deals/:id", crumbs: [SALES, { label: "Sales pipeline", to: "/deals" }, { label: "Deal" }] },
  { path: "/deals", crumbs: [SALES, { label: "Sales pipeline" }] },
  { path: "/quotes/:id", crumbs: [SALES, { label: "Quotes", to: "/quotes" }, { label: "Quote" }] },
  { path: "/quotes", crumbs: [SALES, { label: "Quotes" }] },
  { path: "/proposals/:id", crumbs: [SALES, { label: "Proposals", to: "/proposals" }, { label: "Proposal" }] },
  { path: "/proposals", crumbs: [SALES, { label: "Proposals" }] },
  { path: "/agreements/:id", crumbs: [SALES, { label: "Agreements", to: "/agreements" }, { label: "Agreement" }] },
  { path: "/agreements", crumbs: [SALES, { label: "Agreements" }] },
  { path: "/products", crumbs: [SALES, { label: "Products" }] },

  { path: "/accounts/:id", crumbs: [CUSTOMERS, { label: "Accounts", to: "/accounts" }, { label: "Account" }] },
  { path: "/accounts", crumbs: [CUSTOMERS, { label: "Accounts" }] },
  { path: "/contacts/:id", crumbs: [CUSTOMERS, { label: "Contacts", to: "/contacts" }, { label: "Contact" }] },
  { path: "/contacts", crumbs: [CUSTOMERS, { label: "Contacts" }] },

  { path: "/whatsapp/campaigns/:id", crumbs: [{ label: "Engage" }, { label: "WhatsApp", to: "/whatsapp" }, { label: "Campaign" }] },
  { path: "/whatsapp", crumbs: [{ label: "Engage" }, { label: "WhatsApp" }] },
  { path: "/emails", crumbs: [{ label: "Engage" }, { label: "Email" }] },

  { path: "/reports/user/:id", crumbs: [{ label: "Insights" }, { label: "Reports", to: "/reports" }, { label: "Rep report" }] },
  { path: "/reports", crumbs: [{ label: "Insights" }, { label: "Reports" }] },
  { path: "/targets", crumbs: [{ label: "Insights" }, { label: "Targets" }] },

  { path: "/notifications", crumbs: [{ label: "Notifications" }] },
  { path: "/users", crumbs: [{ label: "Users & roles" }, { label: "Users" }] },
  { path: "/roles", crumbs: [{ label: "Users & roles" }, { label: "Roles & permissions" }] },

  { path: "/settings/company", crumbs: [SETTINGS, { label: "Company" }] },
  { path: "/settings/currencies", crumbs: [SETTINGS, { label: "Currencies & countries" }] },
  { path: "/settings/countries", crumbs: [SETTINGS, { label: "Currencies & countries" }] },
  { path: "/settings/pipelines", crumbs: [SETTINGS, { label: "Pipelines" }] },
  { path: "/settings/rules", crumbs: [SETTINGS, { label: "Business rules" }] },
  { path: "/settings/approvals", crumbs: [SETTINGS, { label: "Approvals" }] },
  { path: "/settings/masters", crumbs: [SETTINGS, { label: "Master lookups" }] },
  { path: "/settings/storage", crumbs: [SETTINGS, { label: "Storage" }] },
  { path: "/settings/ai", crumbs: [SETTINGS, { label: "AI assistant" }] },
  { path: "/settings/sign-in-security", crumbs: [SETTINGS, { label: "Sign-in security" }] },
  { path: "/settings/security-log", crumbs: [SETTINGS, { label: "Security log" }] },
  { path: "/calls", crumbs: [{ label: "Engage" }, { label: "Calls" }] },
  { path: "/visits", crumbs: [{ label: "Engage" }, { label: "Field visits" }] },
  { path: "/settings/preferences", crumbs: [SETTINGS, { label: "Preferences" }] },
  { path: "/settings/notification-rules", crumbs: [SETTINGS, { label: "Notification rules" }] },
  { path: "/settings/calling", crumbs: [SETTINGS, { label: "Calling" }] },
  { path: "/settings/incoming-calls", crumbs: [SETTINGS, { label: "Incoming calls" }] },
  { path: "/settings/whatsapp", crumbs: [SETTINGS, { label: "WhatsApp" }] },
  { path: "/settings/lead-sources", crumbs: [SETTINGS, { label: "Lead sources" }] },
  { path: "/settings/proposal-templates", crumbs: [SETTINGS, { label: "Proposal templates" }] },
  { path: "/settings/quote-design", crumbs: [SETTINGS, { label: "Quote design" }] },
  { path: "/settings/agreement-templates", crumbs: [SETTINGS, { label: "Agreement templates" }] },
  { path: "/settings/email", crumbs: [SETTINGS, { label: "Email" }] },
  { path: "/settings/mailbox", crumbs: [SETTINGS, { label: "Mailbox" }] },
];

export function getCrumbs(pathname: string): Crumb[] {
  for (const r of ROUTES) {
    if (matchPath({ path: r.path, end: true }, pathname)) return r.crumbs;
  }
  return [{ label: "Sales CRM" }];
}
